import { useMemo, useState } from 'react';
import type { TaskSummary } from '@todo/shared';
import { useTaskActions } from '../hooks/useTaskActions';
import { useColumnBoard } from '../hooks/useColumnBoard';
import { useDragToStatus } from '../hooks/useDragToStatus';
import { TaskDetailPanel } from '../components/TaskDetailPanel';
import { BlockReasonModal } from '../components/BlockReasonModal';
import { TaskCard, TaskListError, TaskListEmpty } from '../components/TaskCard';
import { TaskBoardPaged, WORK_COLUMNS } from '../components/TaskBoard';

/**
 * Module 11 - Planned.
 *
 * Open tasks on MY plate with a FUTURE due date, bucketed by how far away
 * they are. Today's work belongs to My Day, not here - the two pages split
 * the timeline at tomorrow's local midnight, so no task appears on both.
 *
 * Each status column loads and pages independently (see useColumnBoard).
 * Done never actually fills here - `open: true` narrows every column's
 * query to PENDING/ONGOING/BLOCKED, so Done just sits empty until a card is
 * marked complete from this page during the current visit.
 */

function localMidnight(daysFromToday: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysFromToday);
}

export default function PlannedPage() {
  const [flash, setFlash] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);

  const filters = useMemo(
    () => ({
      scope: 'assigned-to-me' as const,
      open: true,
      dueFrom: localMidnight(1).toISOString(), // from tomorrow onward
      sort: 'dueAt',
      order: 'asc' as const,
    }),
    [],
  );
  const board = useColumnBoard(WORK_COLUMNS, filters);

  function replace(updated: TaskSummary) {
    board.mutate(updated);
  }

  const actions = useTaskActions(replace, setFlash);
  const drag = useDragToStatus(
    (id) => board.allLoaded.find((t) => t.id === id),
    replace,
    setFlash,
  );

  return (
    <div className="mx-auto px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Planned</h1>
        <p className="mt-1 text-sm text-slate-500">
          {board.loading
            ? 'Loading...'
            : `${board.totalCount} upcoming ${board.totalCount === 1 ? 'task' : 'tasks'} on your plate`}
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <div className="mt-6">
        {board.error ? (
          <TaskListError message={board.error} onRetry={() => void board.reload()} />
        ) : !board.loading && board.totalCount === 0 ? (
          <TaskListEmpty
            title="Nothing planned ahead"
            hint="Tasks assigned to you with a future due date will show up here."
          />
        ) : (
          <TaskBoardPaged
            state={board.state}
            loading={board.loading}
            onLoadMore={(column) => void board.loadMore(column)}
            onDropTask={drag.onDropTask}
            renderCard={(task, subtasks) => (
              <TaskCard
                key={task.id}
                task={task}
                subtasks={subtasks}
                draggable
                busy={actions.busyId === task.id || drag.busyId === task.id}
                onOpen={setViewing}
                onToggleDone={(t) => void actions.toggleDone(t)}
                onToggleImportant={(t) => void actions.toggleImportant(t)}
              />
            )}
          />
        )}
      </div>

      {drag.blocking && (
        <BlockReasonModal
          task={drag.blocking}
          reason={drag.blockReason}
          busy={drag.busyId === drag.blocking.id}
          onReasonChange={drag.setBlockReason}
          onConfirm={drag.confirmBlock}
          onCancel={drag.cancelBlock}
        />
      )}

      {viewing && (
        <TaskDetailPanel
          task={viewing}
          onClose={() => setViewing(null)}
          onChanged={(t) => {
            replace(t);
            setViewing((v) => (v && v.id === t.id ? t : v));
          }}
        />
      )}
    </div>
  );
}
